import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Timer } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Ticks down to a fixed deadline (ms) and fires onExpire once at 0:00.
export function useCountdown(deadline, onExpire) {
  const [left, setLeft] = useState(null);
  const fired = useRef(false);
  const warned = useRef({});
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;
  useEffect(() => {
    if (!deadline) return undefined;
    const tick = () => {
      const s = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setLeft(s);
      if (s <= 300 && s > 60 && !warned.current[5]) { warned.current[5] = true; toast.warning("5 minutes left — your code will be submitted automatically at 0:00"); }
      if (s <= 60 && s > 0 && !warned.current[1]) { warned.current[1] = true; toast.error("1 minute left — finish up"); }
      if (s === 0 && !fired.current) { fired.current = true; expireRef.current?.(); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [deadline]);
  return left;
}

export default function CodingTimer({ left, limitMin }) {
  if (left === null) {
    return (
      <span data-testid="coding-timer" className="inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1.5 rounded-md border border-[color:var(--bd-sub)] text-slate-400">
        <Timer className="w-3.5 h-3.5" /> {limitMin}:00
      </span>
    );
  }
  const tone = left <= 60 ? "border-rose-500/60 bg-rose-500/15 text-rose-300 animate-pulse" : left <= 300 ? "border-amber-500/60 bg-amber-500/15 text-amber-300" : "border-[color:var(--bd-sub)] text-slate-200";
  return (
    <span data-testid="coding-timer" data-left={left} className={`inline-flex items-center gap-1.5 text-xs font-mono font-semibold px-2.5 py-1.5 rounded-md border transition-colors ${tone}`}>
      <Timer className="w-3.5 h-3.5" /> {fmt(left)}
    </span>
  );
}
